/**
 * Sync-Status in der Kopfzeile (T-04, F-44): immer sichtbar - auch wenn die
 * Synchronisierung noch nicht eingerichtet ist. Ein Punkt zeigt offene
 * Konflikte. Antippen oeffnet die Details (am Handy als Panel von unten):
 * Zustand, letzter erfolgreicher Abgleich, wartende Aenderungen, Konflikte
 * und "Jetzt abgleichen". Den Tooltip gibt es am Handy nicht - deshalb
 * stehen die Details im Fenster.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { Cloud, CloudAlert, CloudCheck, CloudOff, CloudSync, RefreshCw, Settings, X } from 'lucide-react';

import { db } from '@/db/schema';
import { jetztAbgleichen, useSync, type SyncZustand } from '@/modules/sync/syncDienst';
import { statusText, vorText } from '@/modules/sync/beschriftung';
import { Ausklapper } from '@/ui/composites/Ausklapper';
import { Button } from '@/ui/primitives/Button';
import { formatDe, toLocalDateTime } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';

const SYMBOL: Record<SyncZustand, typeof Cloud> = {
  aus: Cloud, bereit: CloudCheck, laeuft: CloudSync, offline: CloudOff, fehler: CloudAlert,
};
const FARBE: Record<SyncZustand, string> = {
  aus: 'text-ink-faint', bereit: 'text-ink-muted', laeuft: 'text-ink-muted', offline: 'text-warn', fehler: 'text-danger',
};

export function SyncSymbol() {
  const sync = useSync();
  const konflikte = useLiveQuery(() => db.syncKonflikte.count(), [], 0);
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setJetzt(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const { zustand } = sync;
  const Icon = SYMBOL[zustand];
  const kurz = zustand === 'aus' ? 'nicht eingerichtet' : statusText(zustand, sync.zuletzt, sync.fehler, jetzt);
  const name = `Synchronisierung: ${kurz}${konflikte > 0 ? `, ${konflikte} ${konflikte === 1 ? 'Konflikt' : 'Konflikte'}` : ''}`;
  const schliessen = () => { setOffen(false); knopf.current?.focus(); };

  return (
    <div className="relative">
      <button ref={knopf} type="button" onClick={() => setOffen(!offen)} aria-expanded={offen} aria-haspopup="dialog"
        aria-label={name} title={name}
        className={cn('relative grid size-9 shrink-0 place-items-center rounded-md hover:bg-surface-raised hover:text-ink', FARBE[zustand])}>
        <Icon className={cn('size-4', zustand === 'laeuft' && 'animate-pulse')} />
        {konflikte > 0 && <span aria-hidden="true" className="absolute right-1.5 top-1.5 size-2 rounded-full bg-warn" />}
      </button>
      {offen && (
        <Ausklapper label="Synchronisierung" ausloeser={knopf} onClose={schliessen} className="w-[22rem]">
          <SyncDetails konflikte={konflikte} jetzt={jetzt} onClose={schliessen} />
        </Ausklapper>
      )}
    </div>
  );
}

function SyncDetails({ konflikte, jetzt, onClose }: { konflikte: number; jetzt: number; onClose: () => void }) {
  const { zustand, zuletzt, fehler, fehlerArt, offen, konfig } = useSync();
  const Icon = SYMBOL[zustand];
  const zeile = 'flex items-baseline justify-between gap-3 py-1';

  return (
    <>
      <header className="flex items-center gap-2 border-b border-line px-4 py-2.5">
        <h2 className="flex-1 text-sm font-semibold">Synchronisierung</h2>
        <Link to="/einstellungen#synchronisierung" onClick={onClose} aria-label="Einstellungen der Synchronisierung"
          className="grid size-7 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-ink"><Settings className="size-3.5" /></Link>
        <button type="button" onClick={onClose} aria-label="Schließen"
          className="grid size-7 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-ink"><X className="size-3.5" /></button>
      </header>

      <div className="flex flex-col gap-3 overflow-y-auto px-4 py-3 text-sm">
        {zustand === 'aus' ? (
          <>
            <p className="text-ink-muted">
              Noch nicht eingerichtet. Mit Synchronisierung haben Laptop und Handy dieselben Daten – Ende-zu-Ende-verschlüsselt, offline nutzbar.
            </p>
            <Link to="/einstellungen#synchronisierung" onClick={onClose}
              className="inline-flex h-9 items-center justify-center rounded-md bg-accent px-3.5 text-sm font-medium text-on-accent hover:opacity-90">
              Einrichten
            </Link>
          </>
        ) : (
          <>
            <p role="status" className={cn('flex items-start gap-2', FARBE[zustand])}>
              <Icon className={cn('mt-0.5 size-4 shrink-0', zustand === 'laeuft' && 'animate-pulse')} aria-hidden="true" />
              <span className="text-ink">{statusText(zustand, zuletzt, fehler, jetzt)}</span>
            </p>
            {fehlerArt === 'login' && (
              <p className="rounded-md bg-danger/10 px-3 py-2 text-[12px]">
                Die Anmeldung ist abgelaufen.{' '}
                <button type="button" onClick={() => window.location.reload()} className="font-medium text-accent-text hover:underline">Seite neu laden</button>
              </p>
            )}
            <dl className="flex flex-col divide-y divide-line">
              <div className={zeile}>
                <dt className="text-ink-muted">Letzter Abgleich</dt>
                <dd className="text-right tabular-nums">
                  {zuletzt ? <>{formatDe(toLocalDateTime(new Date(zuletzt)), 'dd.MM. HH:mm')} <span className="text-ink-faint">({vorText(zuletzt, jetzt)})</span></> : 'noch nie'}
                </dd>
              </div>
              <div className={zeile}>
                <dt className="text-ink-muted">Wartende Änderungen</dt>
                <dd className="tabular-nums">{offen > 0 ? offen : 'keine'}</dd>
              </div>
              <div className={zeile}>
                <dt className="text-ink-muted">Konflikte</dt>
                <dd>
                  {konflikte > 0 ? (
                    <Link to="/einstellungen#synchronisierung" onClick={onClose} className="font-medium text-warn hover:underline">
                      {konflikte} klären
                    </Link>
                  ) : 'keine'}
                </dd>
              </div>
              {konfig && (
                <div className={zeile}>
                  <dt className="text-ink-muted">Dieses Gerät</dt>
                  <dd className="min-w-0 truncate">{konfig.geraetName}</dd>
                </div>
              )}
            </dl>
            <Button variant="primary" disabled={zustand === 'laeuft'} onClick={() => void jetztAbgleichen()}>
              <RefreshCw className={cn('size-4', zustand === 'laeuft' && 'animate-spin')} aria-hidden="true" />
              {zustand === 'laeuft' ? 'Wird abgeglichen …' : 'Jetzt abgleichen'}
            </Button>
          </>
        )}
      </div>
    </>
  );
}
